import { Page } from "modules/Page";
import { ArticleListing, ArticleListingApi, articleListingsApi, ArticleListingsQueryParams, CategoryFilter } from "api/ArticleListingApi";
import { Select } from "components/Select/Select";
import { debounce } from "modules/debounce";

import articleListingsTemplate from "./ArticleListingPage.hbs";
import mobileFilterTemplate from "../../components/Filters/FilterMobile.hbs";

enum SelectFilterType {
    Type = "Type",
    Category = "Category",   
}
enum FilterType {
    StringQuery = "q",
    Page = "page",
    Language = "lang"
}

type FilterState = {
    selectedValues: {
        [key in SelectFilterType]: string[];
    };
    [FilterType.StringQuery]: string;
    [FilterType.Page]: number;
    [FilterType.Language]?: string;
};

type State = {
    articleListings: ArticleListing[];
    filters: FilterState;
    totalArticleListings: number;
    totalPages: number;
};

export class ArticleListingPage extends Page<typeof ArticleListingPage> {
    static readonly className = "js-article-listing-page";
    private pageID: string;
    private searchParams: URLSearchParams;
    private articleListLoader: HTMLElement;
    private articleListElement: HTMLElement;
    private loadMoreButton: HTMLButtonElement | null;
    private searchInputElement: HTMLInputElement | null = null;
    private filtersContainer: HTMLElement | null = null;
    
    private selects: { [key in SelectFilterType]: Select | null } = {
        [SelectFilterType.Type]: null,
        [SelectFilterType.Category]: null,
        
    };

    replaceCommaWithPeriod = (str: string): string => {
        return str.replace(/,/g, ".");
    };

    private state: State = {
        articleListings: [] as ArticleListing[],
        filters: {
            selectedValues: {
                [SelectFilterType.Type]: [],
                [SelectFilterType.Category]: [],
                
            },
            [FilterType.StringQuery]: "",
            [FilterType.Page]: 1,
        },
        totalArticleListings: 0,
        totalPages: 0
    }

    constructor(
        rootElement: HTMLElement,
        index: number,
        private readonly api: ArticleListingApi = articleListingsApi,
    ) {
        super(rootElement, index);
                
        this.searchParams = new URLSearchParams(window.location.search);
        this.pageID = (this.rootElement.dataset.pageid || "0");
        const lang = document.getElementsByTagName("html")[0].getAttribute("lang");

        if (lang) {
            this.state.filters[FilterType.Language] = lang;
        }

        this.articleListElement = rootElement.querySelector<HTMLElement>(".article__list .list__section")!;
        this.articleListLoader = rootElement.querySelector<HTMLElement>(".article__list .list__loader")!;
        this.loadMoreButton = rootElement.querySelector<HTMLButtonElement>(".load-more")!;
        this.filtersContainer = rootElement.querySelector(".filters");
        this.searchInputElement = this.rootElement.querySelector<HTMLInputElement>(".filter-search input[type='search']");

        Object.values(SelectFilterType).forEach((selectFilterType) => {
            const select = this.getComponent<Select>(`[name='${selectFilterType}']`);
            this.selects[selectFilterType] = select;
            select?.on("change", this.handleSelectChange(selectFilterType));

            const selectFilterSearchParam = this.searchParams.get(selectFilterType);

            if (!selectFilterSearchParam?.length) return;

            const selectFilterSearchParamArray = selectFilterSearchParam.split(",");
            this.state.filters.selectedValues[selectFilterType] = selectFilterSearchParamArray;
            select?.setSelectedValues(selectFilterSearchParamArray);
        });

        this.loadMoreButton?.addEventListener("click", () => {
            this.loadNextPage();
        });

        this.initFilterGroup();
        this.initSearchFilter();
       
        this.refreshArticleListings();
        this.initCategoriesTabs();
    }

    private handleSelectChange = (filterType: SelectFilterType) => {
        return (selectedValues: string[]) => {
            this.state.filters.selectedValues[filterType] = selectedValues;          

            this.refreshArticleListings();
        }
    }

    initFilterGroup = () => {
        const mainElement = document.querySelector("main");

        const mobileButtonContainer = document.createElement("div");
        const mobileButtonLabel = this.filtersContainer?.dataset.labelFilterMobile || "Filter";

        mobileButtonContainer.classList.add("filter-mobile");
        mobileButtonContainer.style.opacity = '0';
        mobileButtonContainer.id = "filter-mobile";
        mobileButtonContainer.innerHTML = mobileFilterTemplate({ "label": mobileButtonLabel });
        mainElement?.insertAdjacentElement("beforeend", mobileButtonContainer);

        window.addEventListener("scroll", () => {
            mobileButtonContainer.style.opacity = window.scrollY > 10 ? "1" : "0";
        });

        const filtersContainer = document.querySelector(".filters");

        const mobileFilterButton = document.getElementById("filter-mobile");
        const mobileMinimizeFilters = document.getElementById("filters-collapse-mobile");

        mobileFilterButton?.addEventListener("click", () => {
            filtersContainer?.classList.add("active");
            mainElement?.classList.add("disabled");
        });

        mobileMinimizeFilters?.addEventListener("click", () => {
            filtersContainer?.classList.remove("active");
            mainElement?.classList.remove("disabled");
        });

        mainElement?.addEventListener("click", (e: Event) => {
            if (e.target != mainElement) return;

            if (mainElement.classList.contains("disabled")) {
                mainElement.classList.remove("disabled");
                filtersContainer?.classList.remove("active");
            }
        });       

        const filterClearAll = document.getElementById("filter-clear");
        const mobileClearAllFilters = document.getElementById("filter-clear-mobile");

        filterClearAll?.addEventListener("click", this.handleClearAllFilters);
        mobileClearAllFilters?.addEventListener("click", this.handleClearAllFilters);
    }

    handleClearAllFilters = () => {
        for (const select in this.selects) {
            const selectComponent = this.selects[select as SelectFilterType];
            selectComponent?.clearOptions();
            selectComponent?.resetLabels();
        }

        this.resetSearchFilter();            
    }
      
    initSearchFilter = () => {
        const stringQuery = this.searchParams?.get(FilterType.StringQuery);

        if (stringQuery && this.searchInputElement) {
            this.state.filters[FilterType.StringQuery] = stringQuery;
            this.searchInputElement.value = stringQuery;
        }

        const clearSearchButton = this.rootElement.querySelector<HTMLButtonElement>(".filter-search button.clear__button");

        if (!clearSearchButton) return;

        if (this.state.filters[FilterType.StringQuery]?.length > 0) {
            clearSearchButton.style.display = "flex";
        }

        this.searchInputElement?.addEventListener("keyup", () => {
            this.state.filters[FilterType.StringQuery] = this.searchInputElement?.value || "";
            this.refreshArticleListings();

            if (this.searchInputElement?.value) {
                clearSearchButton.style.display = "flex";
            }
            else {
                clearSearchButton.style.display = "none";
            }
        });

        clearSearchButton?.addEventListener("click", () => {
            this.state.filters[FilterType.StringQuery] = "";
            clearSearchButton.style.display = "none";

            if (this.searchInputElement) {
                this.searchInputElement.value = "";
            }

            this.refreshArticleListings();
        });
    }
        
    private resetSearchFilter() {
        if (this.searchInputElement) {
            this.searchInputElement.value = "";
            this.state.filters[FilterType.StringQuery] = "";
        }
    }

    private getArticleListingsHtml(articleListings: ArticleListing[]) {
        return articleListingsTemplate({
            articleListings,
           
        });
    }

    private refreshArticleListings = debounce(async () => {

        this.articleListLoader.style.display = "block";
        this.articleListElement.style.display = "none";

        if (this.loadMoreButton) {
            this.loadMoreButton.style.display = "none";
        }

        this.state.filters[FilterType.Page] = 1;

        const params = this.getRequestParams();
        const articleListingsPageData = await this.api.getArticleListings(params);
        console.log("Article Listings API Response:", articleListingsPageData);

        window.history.replaceState({}, "", `${window.location.pathname}?${new URLSearchParams(params as Record<string, string>)}`);

        this.state.totalArticleListings = articleListingsPageData.totalArticleListings;
        this.state.totalPages = articleListingsPageData.totalPages;
        this.state.articleListings = articleListingsPageData.articleListings;  
              
        if (this.loadMoreButton) {
            if (this.state.totalPages > 1) {
                this.loadMoreButton.style.display = "block";
            } else {
                this.loadMoreButton.style.display = "none";
            }
        }       
        this.updateSelectOptions(SelectFilterType.Type, articleListingsPageData.relevantCategories);

       this.updateSelectOptions(SelectFilterType.Category, articleListingsPageData.relevantCategories);

        this.articleListElement.innerHTML = this.getArticleListingsHtml(this.state.articleListings);

        this.articleListLoader.style.display = "none";
        this.articleListElement.style.display = "grid"; 

    }, 500);

    private updateSelectOptions = (filterType: SelectFilterType, relevantValues?: CategoryFilter[]) => {
        const list = Array.isArray(relevantValues) ? relevantValues : [];

        const select = this.selects[filterType];
        select?.disableAllOptions();
        select?.resetLabels();

        list.forEach(value => {
            const text = value?.text ?? "";
            const urlText = value?.url_text ?? "";

            if (!text) return;

            select?.enableOption(text);

            const currentLabel = select?.getLabel(text) ?? text;
            select?.updateLabel(urlText, `${currentLabel}`);
        });
    };

    private loadNextPage = debounce(async () => {
        this.state.filters[FilterType.Page] += 1;

        if (this.loadMoreButton && this.state.filters[FilterType.Page] == this.state.totalPages) {
            this.loadMoreButton.style.display = "none";
        }

        const params = this.getRequestParams();
        const articleListingsPageData = await this.api.getArticleListings(params);

        this.state.articleListings.push(...articleListingsPageData.articleListings);
       
        this.articleListElement.innerHTML += this.getArticleListingsHtml(articleListingsPageData.articleListings);
    });

    private getRequestParams() {
        const params: ArticleListingsQueryParams = {};
        params[FilterType.Page] = this.state.filters[FilterType.Page];

        Object.values(SelectFilterType).forEach((selectFilterType) => {
            const selectedValues = this.state.filters.selectedValues[selectFilterType];
            if (selectedValues.length) {
                params[selectFilterType] = selectedValues.join(",");
            }
        });

        params[FilterType.StringQuery] = this.state.filters[FilterType.StringQuery];
        params[FilterType.Page] = this.state.filters[FilterType.Page];

        // Add the language parameter
        if (this.state.filters[FilterType.Language]) {
            params.lang = this.state.filters[FilterType.Language];
        }
        if (this.pageID) {
            params.pageID = this.pageID;
        }

        return params;
    } 

    public initCategoriesTabs() {
        const categories = this.rootElement.querySelectorAll(".tab-categories > div");
        const tabButtons = this.rootElement.querySelectorAll(".tab-list li");

        // Handle tab click events
        tabButtons.forEach((tab) => {
            tab.addEventListener("click", () => {
                const isActive = tab.classList.contains("active");
                const tabId = tab.getAttribute("data-item");
                const tabContent = this.rootElement.querySelectorAll(`div[data-item="${tabId}"]`);

                if (isActive) {
                    return;
                }

                // Close all tabs
                tabButtons.forEach((otherTab) => otherTab.classList.remove("active"));
                categories.forEach((category) => category.classList.remove('active'));

                // Open clicked tab if not already active
                if (!isActive) {
                    tab.classList.add("active");
                    tabContent[0].classList.add('active');
                }
            });
        });
    }

}